// 兼容垫片必须最先执行：pdfjs-dist 直接使用 Promise.withResolvers()，
// 旧内核浏览器（Safari < 17.4 / 旧版 WebView 等）缺这个 API，
// 不补的话「PDF 转图片」「强压缩」会报 Promise.withResolvers is not a function
import './polyfill.ts';
import './style.css';
import {
  mergePdfs,
  buildPdfFromImages,
  pdfToImages,
  compressPdfs,
  resizePdfToA4,
  getPageCount,
  splitPdfByRanges,
  downloadBlob,
  pdfBytesToBlob,
  formatBytes,
} from './lib/pdf.ts';
import { setupDropZone, toDropItems, type DropItem } from './components/FileDrop.ts';
import { setupRangeEditor, type RangeRow } from './components/RangeEditor.ts';
import { enableSortable } from './components/DragSort.ts';
import JSZip from 'jszip';
import imageCompression from 'browser-image-compression';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
// 去掉扩展名，仅保留主名，用于拼接导出文件名
const baseName = (name: string) => name.replace(/\.[^.]+$/, '');

/* ---------- 通用：按钮启用状态（list.length 达到 min 才可用） ---------- */
function syncBtn(btn: HTMLButtonElement, list: unknown[], min = 1) {
  btn.disabled = list.length < min;
}

/* ---------- 通用：渲染文件列表（含单条删除 + 可选拖拽排序） ---------- */
function renderList(
  listEl: HTMLElement,
  items: DropItem[],
  totalEl: HTMLElement | null,
  onRemove: (index: number) => void,
  onReorder?: (from: number, to: number) => void,
  describe?: (item: DropItem) => string | null,
) {
  listEl.innerHTML = '';

  items.forEach((it, i) => {
    const li = document.createElement('li');
    // 有 onReorder 时给每行加一个拖柄（触摸端只能从拖柄起手，避免和列表滚动打架）
    if (onReorder) {
      li.classList.add('sortable');
      const h = document.createElement('span');
      h.className = 'handle';
      h.textContent = '⋮⋮';
      h.title = '拖动排序';
      li.appendChild(h);
    }
    const a = document.createElement('span');
    // name 撑满中间、超长省略，行内其它元素才不会被长文件名挤走
    a.className = 'name';
    a.textContent = it.name;
    a.title = it.name;
    const b = document.createElement('span');
    // describe 存在时在尺寸后追加补充信息（如页数），不新增 DOM 结构
    const extra = describe?.(it);
    b.textContent = extra ? `${formatBytes(it.size)} · ${extra}` : formatBytes(it.size);
    const x = document.createElement('button');
    x.className = 'remove';
    x.type = 'button';
    x.textContent = '✕';
    x.title = '移除该文件';
    x.addEventListener('click', () => onRemove(i));
    li.append(a, b, x);
    listEl.appendChild(li);
  });

  // 拖拽排序：松手时把最终顺序回写给调用方（内部对同一元素只绑一次监听）
  if (onReorder) enableSortable(listEl, onReorder);
  if (totalEl) totalEl.textContent = `${items.length} 个文件`;
}

/* ---------- 图片类型白名单（HEIC/AVIF 浏览器不解码） ---------- */
function checkImageSupport(it: DropItem): string | null {
  const heic = it.type === 'image/heic' || /\.heic$/i.test(it.name);
  if (heic) return 'HEIC 图片当前浏览器（Chrome/Edge/Firefox/Safari）都不支持解码，请在手机上转成 JPG 或 PNG 后再试';
  if (it.type === 'image/avif' || /\.avif$/i.test(it.name)) {
    return 'AVIF 图片当前浏览器解码支持有限，请转成 JPG 或 PNG 后再试';
  }
  return null;
}

/* ---------- 状态 ---------- */
const state = {
  merge: [] as DropItem[],
  image: [] as DropItem[],
  convert: [] as DropItem[],
  compress: [] as DropItem[],
  a4: [] as DropItem[],
  split: [] as DropItem[],
  /** 文件 → 页数（分割工具用；页数探测是异步的）。用对象身份做键，避免同名文件跨批次重复时串号 */
  splitPages: new Map<DropItem, number>(),
  /** 文件 → 探测失败原因 */
  splitErrors: new Map<DropItem, string>(),
};

/* ---------- 排序：名称 / 大小，升 / 降（合并 PDF、图片转 PDF 共用） ---------- */
type SortKey = 'name' | 'size';
type SortDir = 1 | -1;

/**
 * 生成一个「按名称 / 按大小」排序器：
 * 同键再点一次 → 反向；换键 → 回到升序。排序后由 render 重画列表。
 */
function createSorter(getItems: () => DropItem[], render: () => void) {
  // key 为 null 表示还没排过，第一次点任何键都应是升序
  const cur = { key: null as SortKey | null, dir: 1 as SortDir };
  return (key: SortKey) => {
    cur.dir = cur.key === key ? ((-cur.dir) as SortDir) : 1;
    cur.key = key;
    const cmp =
      key === 'name'
        ? (a: DropItem, b: DropItem) => a.name.localeCompare(b.name, 'zh-Hans-CN')
        : (a: DropItem, b: DropItem) => a.size - b.size;
    getItems().sort((a, b) => cmp(a, b) * cur.dir);
    render();
  };
}

const sortMerge = createSorter(
  () => state.merge,
  () => renderMerge(),
);

/* ---------- 合并 PDF ---------- */
const mergeDrop = $('mergeDrop');
const mergeList = $('mergeList');
const mergeCount = $('mergeCount');
const mergeBtn = $('mergeBtn') as HTMLButtonElement;
const mergeStatus = $('mergeStatus');
const mergeProgress = $('mergeProgress');

function renderMerge() {
  renderList(
    mergeList,
    state.merge,
    mergeCount,
    (i) => {
      state.merge.splice(i, 1);
      renderMerge();
    },
    (from, to) => {
      const [m] = state.merge.splice(from, 1);
      state.merge.splice(to, 0, m);
      renderMerge();
    },
  );
  syncBtn(mergeBtn, state.merge, 2);
}

setupDropZone(mergeDrop, {
  accept: 'pdf',
  onFiles: async (files) => {
    state.merge.push(...(await toDropItems(files)));
    renderMerge();
  },
});

mergeBtn.addEventListener('click', async () => {
  if (state.merge.length < 2) {
    mergeStatus.textContent = '请至少选择 2 个 PDF';
    return;
  }
  mergeBtn.disabled = true;
  mergeStatus.textContent = '合并中…';
  try {
    const bytes = await mergePdfs(state.merge, (d, t) => {
      mergeProgress.style.width = `${(d / t) * 100}%`;
    });
    downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `${baseName(state.merge[0].name)}-merged.pdf`);
    mergeStatus.textContent = `完成，共 ${state.merge.length} 个文件，${formatBytes(bytes.byteLength)}`;
  } catch (e) {
    mergeStatus.textContent = `失败：${(e as Error).message}`;
  } finally {
    mergeBtn.disabled = false;
    mergeProgress.style.width = '0';
  }
});

$('mergeSortName').addEventListener('click', () => sortMerge('name'));
$('mergeSortSize').addEventListener('click', () => sortMerge('size'));

$('mergeClear').addEventListener('click', () => {
  state.merge = [];
  renderMerge();
  mergeStatus.textContent = '';
});

/* ---------- 图片转 PDF ---------- */
const imageDrop = $('imageDrop');
const imageList = $('imageList');
const imageCount = $('imageCount');
const imageBtn = $('imageBtn') as HTMLButtonElement;
const imageStatus = $('imageStatus');
const imageProgress = $('imageProgress');
const imageQuality = $('imageQuality') as HTMLInputElement;
const imageQualityVal = $('imageQualityVal');

function renderImage() {
  renderList(
    imageList,
    state.image,
    imageCount,
    (i) => {
      state.image.splice(i, 1);
      renderImage();
    },
    // 拖动排序：图片的先后顺序就是 PDF 的页码顺序
    (from, to) => {
      const [m] = state.image.splice(from, 1);
      state.image.splice(to, 0, m);
      renderImage();
    },
  );
  syncBtn(imageBtn, state.image);
}

const sortImage = createSorter(
  () => state.image,
  () => renderImage(),
);

$('imageSortName').addEventListener('click', () => sortImage('name'));
$('imageSortSize').addEventListener('click', () => sortImage('size'));

setupDropZone(imageDrop, {
  accept: 'image',
  onFiles: async (files) => {
    const items = await toDropItems(files);
    const errors: string[] = [];
    for (const it of items) {
      const err = checkImageSupport(it);
      if (err) {
        errors.push(`${it.name}: ${err}`);
        continue;
      }
      // 预压缩：大尺寸非 JPEG 或 任何超过 2.5MB 的图都过一遍 canvas 归一化
      if (it.bytes.length > 2.5 * 1024 * 1024 && it.type !== 'image/jpeg') {
        try {
          const blob = await imageCompression(new Blob([it.bytes], { type: it.type }), {
            maxSizeMB: 2,
            maxWidthOrHeight: 3000,
            useWebWorker: true,
          });
          it.bytes = new Uint8Array(await blob.arrayBuffer());
        } catch {
          errors.push(`${it.name}: 预压缩失败，可能是不支持的图片格式`);
          continue;
        }
      }
      state.image.push(it);
    }
    renderImage();
    if (errors.length) imageStatus.textContent = errors.slice(0, 3).join('；');
  },
});

imageQuality.addEventListener('input', () => {
  imageQualityVal.textContent = imageQuality.value;
});

imageBtn.addEventListener('click', async () => {
  if (!state.image.length) return;
  imageBtn.disabled = true;
  imageStatus.textContent = '生成中…';
  try {
    const q = Number(imageQuality.value) || 0.9;
    const bytes = await buildPdfFromImages(state.image, (d, t) => {
      imageProgress.style.width = `${(d / t) * 100}%`;
    }, q);
    downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `${baseName(state.image[0].name)}-images.pdf`);
    imageStatus.textContent = `完成，${formatBytes(bytes.byteLength)}`;
  } catch (e) {
    const msg = (e as Error).message;
    imageStatus.textContent = `失败：${msg}`;
  } finally {
    imageBtn.disabled = false;
    imageProgress.style.width = '0';
  }
});

$('imageClear').addEventListener('click', () => {
  state.image = [];
  renderImage();
  imageStatus.textContent = '';
});

/* ---------- PDF 转图片 ---------- */
const convertDrop = $('convertDrop');
const convertList = $('convertList');
const convertCount = $('convertCount');
const convertBtn = $('convertBtn') as HTMLButtonElement;
const convertStatus = $('convertStatus');
const convertProgress = $('convertProgress');
const convertFormat = $('convertFormat') as HTMLSelectElement;
const convertScale = $('convertScale') as HTMLSelectElement;

function renderConvert() {
  renderList(convertList, state.convert, convertCount, (i) => {
    state.convert.splice(i, 1);
    renderConvert();
  });
  syncBtn(convertBtn, state.convert);
}

setupDropZone(convertDrop, {
  accept: 'pdf',
  onFiles: async (files) => {
    state.convert.push(...(await toDropItems(files)));
    renderConvert();
  },
});

convertBtn.addEventListener('click', async () => {
  if (!state.convert.length) return;
  convertBtn.disabled = true;
  convertStatus.textContent = '渲染中…';
  const format = convertFormat.value as 'image/png' | 'image/jpeg';
  const scale = Number(convertScale.value);
  const ext = format === 'image/png' ? 'png' : 'jpg';
  const isMultiple = state.convert.length > 1;
  const zip = new JSZip();
  let total = 0;
  // 单文件多页时，把渲染结果留到第二步打包用
  let singleBlobs: Blob[] = [];

  // 第一步：把所有文件的每页渲染成 Blob。
  // - 多文件：同时把 Blob→Uint8Array 写进 JSZip（收集 Promise，稍后统一 await）
  //   因为 JSZip 的 file() 传 Blob 时 generateAsync 不会等内部 FileReader 读完，会打出 0 字节 entry
  // - 单文件：只渲染，留给第二步
  const packPromises: Promise<void>[] = [];
  for (const file of state.convert) {
    const fileIndex = state.convert.indexOf(file);
    const blobs = await pdfToImages(file.bytes, format, 0.9, scale, isMultiple ? (d, t) => {
      const base = fileIndex / state.convert.length;
      convertProgress.style.width = `${(base + (d / t) * (1 / state.convert.length)) * 100}%`;
    } : undefined);
    total += blobs.length;
    if (isMultiple) {
      const dir = file.name.replace(/\.pdf$/i, '');
      const folder = zip.folder(dir) ?? zip;
      for (let i = 0; i < blobs.length; i++) {
        const blob = blobs[i];
        packPromises.push((async () => {
          const buf = new Uint8Array(await blob.arrayBuffer());
          folder.file(`${dir}-p${String(i + 1).padStart(3, '0')}.${ext}`, buf);
        })());
      }
    } else {
      singleBlobs = blobs;
    }
  }
  // 等所有 zip entry 真正写好再打包
  if (isMultiple) await Promise.all(packPromises);

  try {
    if (isMultiple) {
      const out = await zip.generateAsync({ type: 'blob' });
      downloadBlob(out, 'pdf-images.zip');
      convertStatus.textContent = `完成，共 ${total} 页（${state.convert.length} 个文件打包）`;
    } else {
      const file = state.convert[0];
      const base = file.name.replace(/\.pdf$/i, '');
      if (singleBlobs.length === 1) {
        downloadBlob(singleBlobs[0], `${base}.${ext}`);
      } else {
        for (let i = 0; i < singleBlobs.length; i++) {
          const buf = new Uint8Array(await singleBlobs[i].arrayBuffer());
          zip.file(`${base}-p${String(i + 1).padStart(3, '0')}.${ext}`, buf);
        }
        const out = await zip.generateAsync({ type: 'blob' });
        downloadBlob(out, `${base}.zip`);
      }
      convertStatus.textContent = `完成，共 ${total} 页`;
    }
  } catch (e) {
    convertStatus.textContent = `失败：${(e as Error).message}`;
  } finally {
    convertBtn.disabled = false;
    convertProgress.style.width = '0';
  }
});

$('convertClear').addEventListener('click', () => {
  state.convert = [];
  renderConvert();
  convertStatus.textContent = '';
});

/* ---------- 压缩 PDF ---------- */
const compressDrop = $('compressDrop');
const compressList = $('compressList');
const compressCount = $('compressCount');
const compressBtn = $('compressBtn') as HTMLButtonElement;
const compressStatus = $('compressStatus');
const compressProgress = $('compressProgress');
const compressMode = $('compressMode') as HTMLSelectElement;
const compressScale = $('compressScale') as HTMLSelectElement;

function renderCompress() {
  renderList(compressList, state.compress, compressCount, (i) => {
    state.compress.splice(i, 1);
    renderCompress();
  });
  syncBtn(compressBtn, state.compress);
}

setupDropZone(compressDrop, {
  accept: 'pdf',
  onFiles: async (files) => {
    state.compress.push(...(await toDropItems(files)));
    renderCompress();
  },
});

compressBtn.addEventListener('click', async () => {
  if (!state.compress.length) return;
  compressBtn.disabled = true;
  const mode = compressMode.value;
  const scale = Number(compressScale.value) || 1.5;
  compressStatus.textContent =
    mode === 'recompress'
      ? `强压缩中（栅格化 ${Math.round(scale * 72)}dpi，有损）…`
      : '优化中（不重编码图片）…';
  try {
    const totalBefore = state.compress.reduce((s, f) => s + f.size, 0);
    const blobs: Blob[] = [];
    for (let i = 0; i < state.compress.length; i++) {
      const file = state.compress[i];
      let bytes: Uint8Array;
      if (mode === 'recompress') {
        const images = await pdfToImages(file.bytes, 'image/jpeg', 0.72, scale, (d, t) => {
          compressProgress.style.width = `${(d / t) * 100}%`;
        });
        const items = await Promise.all(
          images.map(async (b, idx) => ({
            name: `${file.name}-p${idx}`,
            bytes: new Uint8Array(await b.arrayBuffer()),
            type: 'image/jpeg' as const,
          })),
        );
        bytes = await buildPdfFromImages(items, () => {}, 0.72);
      } else {
        bytes = await compressPdfs([file]);
      }
      blobs.push(new Blob([bytes], { type: 'application/pdf' }));
      compressProgress.style.width = `${((i + 1) / state.compress.length) * 100}%`;
    }
    const totalAfter = blobs.reduce((s, b) => s + b.size, 0);
    if (blobs.length === 1) {
      downloadBlob(blobs[0], `${baseName(state.compress[0].name)}-compressed.pdf`);
    } else {
      const zip = new JSZip();
      state.compress.forEach((f, i) => zip.file(`${baseName(f.name)}-compressed.pdf`, blobs[i]));
      downloadBlob(await zip.generateAsync({ type: 'blob' }), `${baseName(state.compress[0].name)}-compressed.zip`);
    }
    const pct = totalBefore ? ((1 - totalAfter / totalBefore) * 100).toFixed(1) : '0';
    const note = mode === 'optimize' && totalAfter >= totalBefore ? '，未减小（属正常）' : '';
    compressStatus.textContent = `完成：${formatBytes(totalBefore)} → ${formatBytes(totalAfter)}（减少 ${pct}%${note}）`;
  } catch (e) {
    compressStatus.textContent = `失败：${(e as Error).message}`;
  } finally {
    compressBtn.disabled = false;
    compressProgress.style.width = '0';
  }
});

$('compressClear').addEventListener('click', () => {
  state.compress = [];
  renderCompress();
  compressStatus.textContent = '';
});

/* ---------- 调整为 A4 ---------- */
const a4Drop = $('a4Drop');
const a4List = $('a4List');
const a4Count = $('a4Count');
const a4Btn = $('a4Btn') as HTMLButtonElement;
const a4Status = $('a4Status');
const a4Progress = $('a4Progress');
const a4Orientation = $('a4Orientation') as HTMLSelectElement;
const a4Fit = $('a4Fit') as HTMLSelectElement;
const a4Margin = $('a4Margin') as HTMLSelectElement;

function renderA4() {
  renderList(a4List, state.a4, a4Count, (i) => {
    state.a4.splice(i, 1);
    renderA4();
  });
  syncBtn(a4Btn, state.a4);
}

setupDropZone(a4Drop, {
  accept: 'pdf',
  onFiles: async (files) => {
    state.a4.push(...(await toDropItems(files)));
    renderA4();
  },
});

a4Btn.addEventListener('click', async () => {
  if (!state.a4.length) return;
  a4Btn.disabled = true;
  a4Status.textContent = '转换中…';
  try {
    const opts = {
      orientation: a4Orientation.value as 'auto' | 'portrait' | 'landscape',
      fit: a4Fit.value as 'fit' | 'stretch',
      marginMm: Number(a4Margin.value) || 0,
    };
    const blobs: Blob[] = [];
    let totalPages = 0;
    for (let i = 0; i < state.a4.length; i++) {
      const file = state.a4[i];
      const { bytes, pages } = await resizePdfToA4(file.bytes, opts, (d, t) => {
        a4Progress.style.width = `${((i + d / t) / state.a4.length) * 100}%`;
      });
      totalPages += pages;
      blobs.push(new Blob([bytes], { type: 'application/pdf' }));
    }
    if (blobs.length === 1) {
      downloadBlob(blobs[0], `${baseName(state.a4[0].name)}-a4.pdf`);
      a4Status.textContent = `完成，共 ${totalPages} 页，${formatBytes(blobs[0].size)}`;
    } else {
      const zip = new JSZip();
      state.a4.forEach((f, idx) => zip.file(`${baseName(f.name)}-a4.pdf`, blobs[idx]));
      downloadBlob(await zip.generateAsync({ type: 'blob' }), `${baseName(state.a4[0].name)}-a4.zip`);
      a4Status.textContent = `完成，${state.a4.length} 个文件共 ${totalPages} 页，已打包`;
    }
  } catch (e) {
    a4Status.textContent = `失败：${(e as Error).message}`;
  } finally {
    a4Btn.disabled = false;
    a4Progress.style.width = '0';
  }
});

$('a4Clear').addEventListener('click', () => {
  state.a4 = [];
  renderA4();
  a4Status.textContent = '';
});

/* ---------- 分割 PDF ---------- */
const splitDrop = $('splitDrop');
const splitList = $('splitList');
const splitCount = $('splitCount');
const splitBtn = $('splitBtn') as HTMLButtonElement;
const splitStatus = $('splitStatus');
const splitProgress = $('splitProgress');
const splitHint = $('splitHint');

/** 当前有效的切割区间（由 RangeEditor 回调更新） */
let splitRows: RangeRow[] = [];

const rangeEditor = setupRangeEditor($('splitRanges'), (rows) => {
  splitRows = rows;
  updateSplitHint();
  syncSplitBtn();
});

/** 所有已载入文件中的最大页数（范围编辑器的上界） */
function maxLoadedPages(): number | null {
  let max = 0;
  for (const n of state.splitPages.values()) if (n > max) max = n;
  return max > 0 ? max : null;
}

function updateSplitHint() {
  if (!state.split.length) {
    splitHint.textContent = '先添加 PDF 文件，再设置切割区间';
    return;
  }
  const max = maxLoadedPages();
  const fileHint = max ? `文档共 ${max} 页` : '正在读取页数…';
  if (!splitRows.length) {
    splitHint.textContent = `${fileHint} · 请至少添加一个切割区间`;
    return;
  }
  const segs = splitRows.length;
  const files = state.split.length;
  const per = files > 1 ? ` × ${files} 个文件` : '';
  const err = rangeEditor.getError();
  if (err) {
    splitHint.textContent = `${fileHint} · ${err}`;
    return;
  }
  const total = rangeEditor.getTotalPages();
  splitHint.textContent = `${fileHint} · 共 ${segs} 个区间${per} · 将生成 ${segs * files} 个文件（覆盖 ${total} 页/区间）`;
}

function syncSplitBtn() {
  splitBtn.disabled = state.split.length === 0 || splitRows.length === 0;
}

function renderSplit() {
  renderList(
    splitList,
    state.split,
    splitCount,
    (i) => {
      const removed = state.split.splice(i, 1)[0];
      state.splitPages.delete(removed);
      state.splitErrors.delete(removed);
      renderSplit();
      rangeEditor.setMaxPages(maxLoadedPages());
      updateSplitHint();
      syncSplitBtn();
    },
    undefined,
    (it) => {
      const err = state.splitErrors.get(it);
      if (err) return err;
      const pages = state.splitPages.get(it);
      return pages ? `共 ${pages} 页` : '读取中…';
    },
  );
  syncSplitBtn();
}

setupDropZone(splitDrop, {
  accept: 'pdf',
  onFiles: async (files) => {
    const items = await toDropItems(files);
    state.split.push(...items);
    renderSplit();
    updateSplitHint();

    // 异步并行探测页数：探测完成后再更新上界与默认区间
    await Promise.all(
      items.map(async (it) => {
        try {
          const n = await getPageCount(it.bytes);
          state.splitPages.set(it, n);
          state.splitErrors.delete(it);
        } catch (e) {
          state.splitErrors.set(it, (e as Error).message);
        }
        renderSplit();
        rangeEditor.setMaxPages(maxLoadedPages());
        rangeEditor.fillToMaxIfUntouched();
        updateSplitHint();
      }),
    );
  },
});

splitBtn.addEventListener('click', async () => {
  if (!state.split.length || !splitRows.length) return;

  // 校验区间
  const err = rangeEditor.getError();
  if (err) {
    splitStatus.textContent = `区间有误：${err}`;
    return;
  }

  splitBtn.disabled = true;
  splitStatus.textContent = '分割中…';
  splitProgress.style.width = '0';

  try {
    const totalFiles = state.split.length;
    const outputs: Array<{ path: string; bytes: Uint8Array }> = [];
    const notes: string[] = [];
    let totalPages = 0;

    for (let i = 0; i < state.split.length; i++) {
      const file = state.split[i];
      const base = file.name.replace(/\.pdf$/i, '');

      const res = await splitPdfByRanges(file.bytes, splitRows, base, (d, t) => {
        splitProgress.style.width = `${((i + d / t) / totalFiles) * 100}%`;
      });
      totalPages += res.totalPages;
      if (res.skipped > 0) {
        notes.push(`${base}：${res.skipped} 个区间超出 ${res.totalPages} 页，已跳过`);
      }
      for (const tr of res.truncated) {
        notes.push(
          `${base}：区间 ${tr.from.start}-${tr.from.end} 超出 ${res.totalPages} 页，已截短为 ${tr.to.start}-${tr.to.end}`,
        );
      }

      for (const seg of res.segments) {
        // 单文件时文件名带原名前缀；多文件时放进以原名命名的文件夹
        const path = totalFiles > 1 ? `${base}/${seg.name}` : `${base}-${seg.name}`;
        outputs.push({ path, bytes: seg.bytes });
      }
    }

    if (outputs.length === 0) {
      splitStatus.textContent = '没有可输出的区间（所有区间都超出了各文件的页数）';
      return;
    }

    if (outputs.length === 1) {
      // 单个结果直接下载
      const o = outputs[0];
      downloadBlob(pdfBytesToBlob(o.bytes), o.path.split('/').pop()!);
      splitStatus.textContent = `完成，已生成 1 个 PDF（${splitRows[0].start}-${splitRows[0].end} 页）`;
    } else {
      // 多个结果打包 ZIP
      // 注意：这里直接喂 Uint8Array（pdf-lib 的原生返回），不能传 Blob
      // ——JSZip 收到 Blob 时 generateAsync 不会等内部 FileReader 读完，会写出 0 字节 entry
      const zip = new JSZip();
      for (const o of outputs) zip.file(o.path, o.bytes);
      const out = await zip.generateAsync({ type: 'blob' });
      downloadBlob(out, `${baseName(state.split[0].name)}-split.zip`);
      splitStatus.textContent = `完成，${totalFiles} 个文件共 ${totalPages} 页，已生成 ${outputs.length} 个 PDF`;
    }

    if (notes.length) {
      splitStatus.textContent += `；${notes.join('；')}`;
    }
  } catch (e) {
    splitStatus.textContent = `失败：${(e as Error).message}`;
  } finally {
    splitBtn.disabled = false;
    splitProgress.style.width = '0';
  }
});

$('splitClear').addEventListener('click', () => {
  state.split = [];
  state.splitPages.clear();
  state.splitErrors.clear();
  splitRows = [];
  rangeEditor.reset();
  rangeEditor.setMaxPages(null);
  renderSplit();
  updateSplitHint();
  syncSplitBtn();
  splitStatus.textContent = '';
});
